import Sidebar from "../../Components/Sidebar/Sidebar";
import Header from "../../Components/Header/Header";
import classes from "./Doctors.module.css";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
    FaUsers,
    FaUserPlus,
    FaFileImport,
    FaFileExport,
    FaStethoscope,
    FaSearch,
    FaFilter,
    FaRedoAlt,
    FaEllipsisV,
    FaEye,
    FaEdit,
    FaMars,
    FaVenus,
    FaSpinner,
    FaStar,
    FaStarHalfAlt,
    FaRegStar
} from "react-icons/fa";
import { useState, useEffect, useMemo } from "react";
import { BASE_URL } from "../../Components/API/Api";

// --- SKELETON LOADER COMPONENTS ---
const SkeletonStatsCard = () => (
    <div className={classes.skeletonCard}>
        <div className={classes.skeletonIcon}></div>
        <div className={classes.skeletonCardInfo}>
            <div className={classes.skeletonText}></div>
            <div className={classes.skeletonText} style={{ width: '60%' }}></div>
            <div className={classes.skeletonText} style={{ width: '40%' }}></div>
        </div>
    </div>
);

const SkeletonRow = () => (
    <tr className={classes.skeletonRow}>
        <td>
            <div className={classes.skeletonDoctorInfo}>
                <div className={classes.skeletonAvatar}></div>
                <div>
                    <div className={classes.skeletonText} style={{ width: '120px' }}></div>
                    <div className={classes.skeletonText} style={{ width: '80px' }}></div>
                </div>
            </div>
        </td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonText}></div></td>
        <td><div className={classes.skeletonBadge}></div></td>
        <td>
            <div className={classes.skeletonActions}>
                <div className={classes.skeletonActionBtn}></div>
                <div className={classes.skeletonActionBtn}></div>
                <div className={classes.skeletonActionBtn}></div>
            </div>
        </td>
    </tr>
);

function Doctors() {
    const navigate = useNavigate();

    const [doctors, setDoctors] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [search, setSearch] = useState("");
    const [department, setDepartment] = useState("");
    const [specialization, setSpecialization] = useState("");
    const [status, setStatus] = useState("");
    const [currentPage, setCurrentPage] = useState(1);
    const [stats, setStats] = useState([
        {
            title: "Total Doctors",
            value: "0",
            link: "View all doctors",
            icon: <FaStethoscope />,
            color: "#2F80ED",
            bg: "#EDF5FF",
        },
        {
            title: "Active Doctors",
            value: "0",
            link: "View active",
            icon: <FaUserPlus />,
            color: "#27AE60",
            bg: "#ECFFF5",
        },
        {
            title: "Inactive Doctors",
            value: "0",
            link: "View inactive",
            icon: <FaUsers />,
            color: "#F2994A",
            bg: "#FFF6EB",
        },
    ]);

    const rowsPerPage = 5;

    // Fetch doctors from API
    const fetchDoctors = async () => {
        setLoading(true);
        setError("");
        try {
            const companyId = localStorage.getItem("companyId") || "1";

            const response = await axios.get(`${BASE_URL}/api/doctor`, {
                params: {
                    company_id: parseInt(companyId),
                },
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${localStorage.getItem('authToken')}`,
                },
            });

            console.log("Doctors API Response:", response.data);

            let doctorsData = [];
            if (response.data && response.data.data) {
                if (Array.isArray(response.data.data)) {
                    doctorsData = response.data.data;
                } else if (response.data.data.doctors) {
                    doctorsData = response.data.data.doctors;
                } else {
                    doctorsData = [response.data.data];
                }
            }

            setDoctors(doctorsData);
            updateStats(doctorsData);

        } catch (error) {
            console.error("Error fetching doctors:", error);
            let errorMsg = "Failed to load doctors. Please try again.";
            if (error.response) {
                if (error.response.status === 401) {
                    errorMsg = "Unauthorized. Please login again.";
                } else if (error.response.data?.message) {
                    const message = error.response.data.message;
                    if (typeof message === 'object') {
                        const firstKey = Object.keys(message)[0];
                        if (firstKey && Array.isArray(message[firstKey])) {
                            errorMsg = message[firstKey][0];
                        } else {
                            errorMsg = JSON.stringify(message);
                        }
                    } else if (typeof message === 'string') {
                        errorMsg = message;
                    }
                }
            } else if (error.request) {
                errorMsg = "Unable to connect to server. Please check your internet connection.";
            }
            setError(errorMsg);
        } finally {
            setLoading(false);
        }
    };

    // Update stats
    const updateStats = (doctorsData) => {
        const total = doctorsData.length;
        const active = doctorsData.filter(d => d.status?.toLowerCase() === "active").length;
        const inactive = doctorsData.filter(d => d.status?.toLowerCase() === "inactive").length;

        setStats([
            {
                title: "Total Doctors",
                value: total.toString(),
                link: "View all doctors",
                icon: <FaStethoscope />,
                color: "#2F80ED",
                bg: "#EDF5FF",
            },
            {
                title: "Active Doctors",
                value: active.toString(),
                link: "View active",
                icon: <FaUserPlus />,
                color: "#27AE60",
                bg: "#ECFFF5",
            },
            {
                title: "Inactive Doctors",
                value: inactive.toString(),
                link: "View inactive",
                icon: <FaUsers />,
                color: "#F2994A",
                bg: "#FFF6EB",
            },
        ]);
    };

    useEffect(() => {
        fetchDoctors();
    }, []);

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);
            return date.toLocaleDateString('en-GB', {
                day: '2-digit',
                month: 'short',
                year: 'numeric'
            });
        } catch {
            return dateString;
        }
    };

    const getAvatar = (doctor) => {
        if (doctor.avatar) return doctor.avatar;
        const gender = doctor.gender?.toLowerCase() === "female" ? "women" : "men";
        const id = doctor.id || Math.floor(Math.random() * 100);
        return `https://randomuser.me/api/portraits/${gender}/${id % 70 + 1}.jpg`;
    };

    const getInitials = (name) => {
        if (!name) return "?";
        const parts = name.split(' ');
        if (parts.length >= 2) {
            return parts[0][0] + parts[1][0];
        }
        return name.substring(0, 2).toUpperCase();
    };

    const getFullName = (doctor) => {
        const firstName = doctor.first_name || '';
        const lastName = doctor.last_name || '';
        const name = doctor.name || '';
        return name || `${firstName} ${lastName}`.trim() || "Unknown";
    };

    // Get unique departments for filter
    const departments = useMemo(() => {
        const depts = new Set();
        doctors.forEach(d => {
            if (d.department) depts.add(d.department);
        });
        return Array.from(depts);
    }, [doctors]);

    // Get unique specializations for filter
    const specializations = useMemo(() => {
        const specs = new Set();
        doctors.forEach(d => {
            if (d.specialization) specs.add(d.specialization);
        });
        return Array.from(specs);
    }, [doctors]);

    // Get unique statuses for filter
    const statuses = useMemo(() => {
        const stat = new Set();
        doctors.forEach(d => {
            if (d.status) stat.add(d.status);
        });
        return Array.from(stat);
    }, [doctors]);

    const filteredDoctors = useMemo(() => {
        return doctors.filter((doctor) => {
            const keyword = search.toLowerCase();
            const fullName = getFullName(doctor).toLowerCase();

            const searchMatch =
                doctor.first_name?.toLowerCase().includes(keyword) ||
                doctor.last_name?.toLowerCase().includes(keyword) ||
                doctor.email?.toLowerCase().includes(keyword) ||
                doctor.phone?.toLowerCase().includes(keyword) ||
                fullName.includes(keyword);

            const departmentMatch =
                department === "" ||
                doctor.department === department;

            const specializationMatch =
                specialization === "" ||
                doctor.specialization === specialization;

            const statusMatch =
                status === "" ||
                doctor.status === status;

            return (
                searchMatch &&
                departmentMatch &&
                specializationMatch &&
                statusMatch
            );
        });
    }, [doctors, search, department, specialization, status]);

    const totalPages = Math.ceil(filteredDoctors.length / rowsPerPage);
    const indexOfLast = currentPage * rowsPerPage;
    const indexOfFirst = indexOfLast - rowsPerPage;
    const currentDoctors = filteredDoctors.slice(indexOfFirst, indexOfLast);

    const goToPage = (page) => {
        if (page >= 1 && page <= totalPages) {
            setCurrentPage(page);
        }
    };

    const handleView = (doctor) => {
        console.log("Viewing:", doctor);
        navigate(`/doctor/${doctor.id}`);
    };

    const handleEdit = (doctor) => {
        console.log("Editing:", doctor);
        navigate(`/edit-doctor/${doctor.id}`);
    };

    const resetFilters = () => {
        setSearch("");
        setDepartment("");
        setSpecialization("");
        setStatus("");
        setCurrentPage(1);
    };

    const renderSkeletonRows = () => {
        return Array(5).fill(0).map((_, index) => (
            <SkeletonRow key={`skeleton-${index}`} />
        ));
    };

    const renderSkeletonStats = () => {
        return Array(3).fill(0).map((_, index) => (
            <SkeletonStatsCard key={`skeleton-stat-${index}`} />
        ));
    };

    // Render star rating
    const renderStars = (rating) => {
        if (!rating) return null;
        const fullStars = Math.floor(rating);
        const hasHalfStar = rating % 1 >= 0.5;
        const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

        return (
            <span className={classes.stars}>
                {[...Array(fullStars)].map((_, i) => (
                    <FaStar key={`full-${i}`} className={classes.starFilled} />
                ))}
                {hasHalfStar && <FaStarHalfAlt className={classes.starHalf} />}
                {[...Array(emptyStars)].map((_, i) => (
                    <FaRegStar key={`empty-${i}`} className={classes.starEmpty} />
                ))}
            </span>
        );
    };

    return (
        <div className={classes.page}>
            <Sidebar />
            <div className={classes.main}>
                <Header />
                <div className={classes.content}>
                    {/* TOP SECTION */}
                    <div className={classes.top}>
                        <div>
                            <h2>Doctors</h2>
                            <div className={classes.breadcrumb}>
                                Dashboard
                                <span>&gt;</span>
                                Doctors
                            </div>
                        </div>
                        <div className={classes.actions}>
                            <button className={classes.outline}>
                                <FaFileImport />
                                Import Doctors
                            </button>
                            <button className={classes.outline}>
                                <FaFileExport />
                                Export
                            </button>
                            <button
                                className={classes.primary}
                                onClick={() => navigate("/addDoctor")}
                            >
                                + Add New Doctor
                            </button>
                        </div>
                    </div>

                    {/* STATS CARDS */}
                    <div className={classes.cards}>
                        {loading ? (
                            renderSkeletonStats()
                        ) : (
                            stats.map((item, index) => (
                                <div className={classes.card} key={index}>
                                    <div
                                        className={classes.icon}
                                        style={{
                                            background: item.bg,
                                            color: item.color
                                        }}
                                    >
                                        {item.icon}
                                    </div>
                                    <div className={classes.cardInfo}>
                                        <p>{item.title}</p>
                                        <h2>{item.value}</h2>
                                        <button>{item.link}</button>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>

                    {/* SEARCH & FILTERS */}
                    <div className={classes.searchContainer}>
                        <div className={classes.searchBox}>
                            <FaSearch className={classes.searchIcon} />
                            <input
                                type="text"
                                placeholder="Search by Name, Email, Phone..."
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                                disabled={loading}
                            />
                        </div>

                        <select
                            value={department}
                            onChange={(e) => setDepartment(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">All Departments</option>
                            {departments.map((dept) => (
                                <option key={dept} value={dept}>{dept}</option>
                            ))}
                        </select>

                        <select
                            value={specialization}
                            onChange={(e) => setSpecialization(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">All Specializations</option>
                            {specializations.map((spec) => (
                                <option key={spec} value={spec}>{spec}</option>
                            ))}
                        </select>

                        <select
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                            disabled={loading}
                        >
                            <option value="">All Status</option>
                            {statuses.map((stat) => (
                                <option key={stat} value={stat}>{stat}</option>
                            ))}
                        </select>

                        <button
                            className={classes.resetBtn}
                            onClick={resetFilters}
                            disabled={loading}
                        >
                            <FaRedoAlt />
                            Reset
                        </button>
                    </div>

                    {/* ERROR MESSAGE */}
                    {error && (
                        <div className={classes.errorMessage}>
                            <span>{error}</span>
                            <button onClick={fetchDoctors} className={classes.retryBtn}>
                                Retry
                            </button>
                        </div>
                    )}

                    {/* TABLE */}
                    <div className={classes.tableContainer}>
                        {loading ? (
                            <table className={classes.table}>
                                <thead>
                                    <tr>
                                        <th>Doctor</th>
                                        <th>Email</th>
                                        <th>Specialization</th>
                                        <th>Sub-Specialization</th>
                                        <th>Phone num</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {renderSkeletonRows()}
                                </tbody>
                            </table>
                        ) : (
                            <table className={classes.table}>
                                <thead>
                                    <tr>
                                        <th>Doctor</th>
                                        <th>Email</th>
                                        <th>Specialization</th>
                                        <th>Sub-Specialization</th>
                                        <th>Phone num</th>
                                        <th>Status</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {currentDoctors.length > 0 ? (
                                        currentDoctors.map((doctor) => {
                                            const fullName = getFullName(doctor);
                                            const avatar = getAvatar(doctor);

                                            return (
                                                <tr key={doctor.id}>
                                                    <td>
                                                        <div className={classes.doctorInfo}>
                                                            <img
                                                                src={avatar}
                                                                alt={fullName}
                                                                className={classes.avatar}
                                                                onError={(e) => {
                                                                    e.target.style.display = 'none';
                                                                    e.target.nextSibling.style.display = 'flex';
                                                                }}
                                                            />
                                                            <div className={classes.avatarFallback} style={{ display: 'none' }}>
                                                                {getInitials(fullName)}
                                                            </div>
                                                            <div>
                                                                <h4>{fullName}</h4>
                                                                <span>{doctor.date_of_birth || "N/A"}</span>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>{doctor.email || "N/A"}</td>
                                                    <td>{doctor.specialization || "N/A"}</td>
                                                    <td>{doctor.sub_specialization || "N/A"}</td>
                                                    <td>{doctor.phone_number}</td>
                                                    <td>
                                                        <span
                                                            className={`${classes.badge}
                                                            ${doctor.status?.toLowerCase() === "active"
                                                                    ? classes.active
                                                                    : doctor.status?.toLowerCase() === "inactive"
                                                                        ? classes.inactive
                                                                        : classes.pending
                                                                }`}
                                                        >
                                                            {doctor.status || "Pending"}
                                                        </span>
                                                    </td>
                                                    <td>
                                                        <div className={classes.tableActions}>
                                                            <button
                                                                onClick={() => handleView(doctor)}
                                                                className={classes.viewBtn}
                                                                title="View Doctor"
                                                            >
                                                                <FaEye />
                                                            </button>
                                                            <button
                                                                onClick={() => handleEdit(doctor)}
                                                                className={classes.editBtn}
                                                                title="Edit Doctor"
                                                            >
                                                                <FaEdit />
                                                            </button>
                                                            <button
                                                                className={classes.moreBtn}
                                                                title="More Options"
                                                            >
                                                                <FaEllipsisV />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })
                                    ) : (
                                        <tr>
                                            <td colSpan="7" style={{ textAlign: "center", padding: "40px" }}>
                                                No Doctors Found
                                            </td>
                                        </tr>
                                    )}
                                </tbody>
                            </table>
                        )}
                    </div>

                    {/* PAGINATION */}
                    {!loading && filteredDoctors.length > 0 && (
                        <div className={classes.pagination}>
                            <p>
                                Showing
                                <strong>
                                    {" "}
                                    {filteredDoctors.length > 0 ? indexOfFirst + 1 : 0}
                                    -
                                    {Math.min(indexOfLast, filteredDoctors.length)}
                                </strong>
                                {" "}of{" "}
                                <strong>{filteredDoctors.length}</strong>
                                doctors
                            </p>
                            <div className={classes.pages}>
                                <button
                                    onClick={() => goToPage(currentPage - 1)}
                                    disabled={currentPage === 1}
                                >
                                    Previous
                                </button>
                                {Array.from({ length: totalPages }, (_, i) => (
                                    <button
                                        key={i}
                                        onClick={() => goToPage(i + 1)}
                                        className={currentPage === i + 1 ? classes.activePage : ""}
                                    >
                                        {i + 1}
                                    </button>
                                ))}
                                <button
                                    onClick={() => goToPage(currentPage + 1)}
                                    disabled={currentPage === totalPages}
                                >
                                    Next
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

export default Doctors;